import { z } from "zod";
import type { AppTranslateFn } from "@/hooks/use-app-translation";
import { buildLayoutFieldTypeIndex } from "@/lib/layoutRenderer/utils/layout-field-type-index.util";
import { API_NUMBER_DATA_TYPES } from "../constants/api-number-data-type.constant";
import { FIELD_TYPES } from "../constants/dynamicForm.constant";
import type {
	DynamicFormSchema,
	FormField,
	GroupField,
	NumberField,
	ZodSchemaResult,
} from "../interfaces/dynamicForm.interface";
import { timeOfDaySeconds } from "./fieldValidators.util";
import { persistedDefinitionToFormField } from "./persisted-field-definition.util";
import { storedFileId } from "./stored-file-id.util";
import { storedStringList } from "./stored-string-list.util";

export function generateZodSchema(
	schema: DynamicFormSchema,
	t: AppTranslateFn,
	options?: { enforceRequired?: boolean },
): ZodSchemaResult {
	const enforceRequired = options?.enforceRequired !== false;
	const layoutFieldTypeByDefinitionKey = buildLayoutFieldTypeIndex(
		schema.layoutJson,
	);
	const shape: Record<string, z.ZodTypeAny> = {};
	for (const def of schema.fieldDefinitions) {
		const field = persistedDefinitionToFormField(def, {
			layoutFieldTypeByDefinitionKey,
		});
		if (!field) continue;
		shape[field.name] = mapField(field, t, enforceRequired);
	}
	return z.object(shape);
}

/**
 * String schema that skips minLength/pattern when empty (optional), matching fieldValidators.
 */
function mapConstrainedStringField(
	field: FormField,
	t: AppTranslateFn,
): z.ZodTypeAny {
	const validation =
		field.type === "text" || field.type === "url" || field.type === "textArea"
			? field.validation
			: undefined;
	const minLength = validation?.minLength;
	const maxLength = validation?.maxLength;
	const pattern = validation?.pattern;
	const patternMessage =
		validation?.custom || t("common.validation.patternMismatch");

	return z.string().superRefine((val, ctx) => {
		if (!val) {
			if (field.required) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: t("common.validation.requiredShort"),
				});
			}
			return;
		}

		if (minLength != null && val.length < minLength) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: t("common.validation.minLength", { count: minLength }),
			});
		}
		if (maxLength != null && val.length > maxLength) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: t("common.validation.maxLength", { count: maxLength }),
			});
		}
		if (pattern) {
			try {
				if (!new RegExp(pattern).test(val)) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: patternMessage,
					});
				}
			} catch {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: t("common.validation.invalidPattern"),
				});
			}
		}
	});
}

/**
 * Number schema that allows empty when optional (and coerces numeric strings).
 */
function mapOptionalNumberSchema(
	field: NumberField,
	t: AppTranslateFn,
): z.ZodTypeAny {
	let num = z.number();
	if (field.apiNumberDataType === API_NUMBER_DATA_TYPES.Integer) {
		num = num.int();
	}
	if (field.validation?.min != null) {
		num = num.min(
			field.validation.min,
			t("common.validation.minNumber", { count: field.validation.min }),
		);
	}
	if (field.validation?.max != null) {
		num = num.max(
			field.validation.max,
			t("common.validation.maxNumber", { count: field.validation.max }),
		);
	}
	return field.required ? num : num.optional();
}

/**
 * Date/time schema that allows empty when optional, matching fieldValidators.
 */
function mapDateTimeField(field: FormField, t: AppTranslateFn): z.ZodTypeAny {
	return z.string().superRefine((val, ctx) => {
		if (!val) {
			if (field.required) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: t("common.validation.required"),
				});
			}
			return;
		}
		const invalid =
			field.type === "time"
				? timeOfDaySeconds(val) == null
				: Number.isNaN(Date.parse(val));
		if (invalid) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: t("common.validation.dateTimeInvalid"),
			});
		}
	});
}

function mapField(
	field: FormField,
	t: AppTranslateFn,
	enforceRequired = true,
): z.ZodTypeAny {
	if (!enforceRequired) {
		field = { ...field, required: false } as FormField;
	}
	switch (field.type) {
		case FIELD_TYPES.TEXT:
		case FIELD_TYPES.TELEPHONE as "phone":
		case FIELD_TYPES.URL as "url":
			return mapConstrainedStringField(field, t);
		case FIELD_TYPES.NUMBER as "number": {
			const nf = field as NumberField;
			return z.preprocess(
				(raw) => {
					if (raw === "" || raw === null || raw === undefined) return undefined;
					if (typeof raw === "string" && raw.trim() !== "") {
						const n = Number(raw);
						return Number.isNaN(n) ? raw : n;
					}
					return raw;
				},
				mapOptionalNumberSchema(nf, t),
			);
		}
		case FIELD_TYPES.DATE_PICKER as "date":
		case FIELD_TYPES.TIME_PICKER as "time":
		case FIELD_TYPES.DATETIME_PICKER as "dateTime":
			return mapDateTimeField(field, t);
		case FIELD_TYPES.CHECKBOX as "checkbox":
			return z.boolean();
		case FIELD_TYPES.RADIO as "radio": {
			const allowed = field.options.map((o) => o.value);
			if (allowed.length === 0) {
				return field.required
					? z.string().min(1, t("common.validation.selectRequired"))
					: z.string();
			}
			if (field.required) {
				return z
					.string()
					.min(1, t("common.validation.selectRequired"))
					.refine((v) => allowed.includes(v), {
						message: t("common.validation.invalidSelection"),
					});
			}
			return z.string().refine((v) => v === "" || allowed.includes(v), {
				message: t("common.validation.invalidSelection"),
			});
		}
		case FIELD_TYPES.SELECT as "select": {
			const allowed = field.options.map((o) => o.value);
			if (field.multiple) {
				let arr = z.array(z.string());
				if (field.required) {
					arr = arr.min(1, t("common.validation.selectAtLeastOne"));
				}
				return arr.refine((a) => a.every((v) => allowed.includes(v)), {
					message: t("common.validation.invalidSelection"),
				});
			}
			if (allowed.length === 0) {
				return field.required
					? z.string().min(1, t("common.validation.selectRequired"))
					: z.string();
			}
			if (field.required) {
				return z
					.string()
					.min(1, t("common.validation.selectRequired"))
					.refine((v) => allowed.includes(v), {
						message: t("common.validation.invalidSelection"),
					});
			}
			return z.string().refine((v) => v === "" || allowed.includes(v), {
				message: t("common.validation.invalidSelection"),
			});
		}
		case FIELD_TYPES.SLIDER as "slider":
			return z
				.number()
				.min(field.min, t("common.validation.minNumber", { count: field.min }))
				.max(field.max, t("common.validation.maxNumber", { count: field.max }));
		case FIELD_TYPES.SWITCH as "switch":
			return z.boolean();
		case FIELD_TYPES.TOGGLE_BUTTON as "toggleButton":
			if (field.exclusive) {
				return field.required
					? z.string().min(1, t("common.validation.selectRequired"))
					: z.string();
			}
			if (field.required) {
				return z
					.array(z.string())
					.min(1, t("common.validation.selectAtLeastOne"));
			}
			return z.array(z.string());
		case FIELD_TYPES.AUTOCOMPLETE as "autocomplete":
			if (field.multiple) {
				if (field.required) {
					return z
						.array(z.string())
						.min(1, t("common.validation.selectAtLeastOne"));
				}
				return z.array(z.string());
			}
			return field.required
				? z.string().min(1, t("common.validation.requiredShort"))
				: z.string();
		case FIELD_TYPES.GROUP as "group": {
			const g = field as GroupField;
			const nestedShape: Record<string, z.ZodTypeAny> = {};
			for (const child of g.fields) {
				nestedShape[child.name] = mapField(child, t, enforceRequired);
			}
			return z.object(nestedShape);
		}
		case FIELD_TYPES.MULTI_SELECT as "multiSelect":
		case FIELD_TYPES.USERS as "users": {
			const allowed =
				field.type === "multiSelect"
					? field.options.map((option) => option.value)
					: null;
			return z.preprocess(
				(raw) => storedStringList(raw),
				z.array(z.string()).superRefine((values, ctx) => {
					if (field.required && values.length === 0) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: t("common.validation.selectAtLeastOne"),
						});
					}
					if (
						allowed &&
						values.some((value) => !allowed.includes(value))
					) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: t("common.validation.invalidSelection"),
						});
					}
				}),
			);
		}
		case FIELD_TYPES.ADDRESS as "address":
			return z.string().superRefine((val, ctx) => {
				if (val.trim() === "" && field.required) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: t("common.validation.requiredShort"),
					});
				}
			});
		case FIELD_TYPES.SIGNATURE as "signature":
			return z.preprocess(
				(raw) => (typeof raw === "string" ? raw : ""),
				z.string().superRefine((val, ctx) => {
					if (val.trim() === "" && field.required) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: t("common.validation.fileRequired"),
						});
					}
				}),
			);
		case FIELD_TYPES.FILE_UPLOAD as "file":
			return z.preprocess(
				(raw) => {
					if (raw == null || (Array.isArray(raw) && raw.length === 0)) {
						return "";
					}
					return storedFileId(raw) ?? raw;
				},
				z.string().superRefine((val, ctx) => {
					if (val.trim() === "" && field.required) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: t("common.validation.fileRequired"),
						});
					}
				}),
			);
		default:
			return z.any();
	}
}
